import { EyeOutlined, SearchOutlined } from '@ant-design/icons'
import { Button, Card, Row, Space, Table, Input } from 'antd'
import { useMemo, useState, type FC } from 'react'
import { Link } from 'react-router-dom'
import { LocalDateTime } from '~/components/LocalDateTime'
import { useTableDataSourceKey } from '~/hooks/useTableDataSourceKey'
import { type Course } from '~/types/course'
import { compareDate, compareNumber, compareText } from '~/utils/helpers'

interface CourseTableProps {
  datasource: Course[] | null
  isLoading: boolean
}

const CourseTable: FC<CourseTableProps> = ({ datasource, isLoading }) => {
  const data = useTableDataSourceKey(datasource)
  const [filterValue, setFilterValue] = useState('')

  const filteredDatasource = useMemo<Course[]>(() => {
    if (data == null) {
      return []
    }

    return data
      .filter(
        (course) => course.name
          .toLowerCase()
          .includes(filterValue.toLowerCase())
      )
  }, [filterValue, data])

  return (
    <>
      <Card extra={[
        <Input.Search
          key={1}
          placeholder="Search"
          allowClear
          enterButton={<SearchOutlined />}
          onSearch={setFilterValue}
        />
      ]}>
        <Table
          size="small"
          tableLayout="auto"
          dataSource={filteredDatasource}
          loading={isLoading}
          footer={() => (
            <Row justify="start">
              <span>Total: {filteredDatasource.length} courses</span>
            </Row>
          )}
          columns={[
            {
              title: 'ID',
              dataIndex: 'id',
              sorter: (a, b) => compareNumber(a.id, b.id)
            },
            {
              title: 'Title',
              render: (_, course) => <Link to={`${course.id}`}>{course.name}</Link>,
              sorter: (a, b) => compareText(a.name, b.name)
            },
            {
              title: 'Instructors',
              render: (_, course) => {
                if (course.instructors == null) {
                  return ''
                }

                return (
                  <div>
                    {course.instructors.map((i) => (
                      <div key={i.id}>- {i.name}</div>
                    ))}
                  </div>
                )
              }
            },
            {
              title: 'CAS supports',
              render: (_, course) => {
                if (course.cas_supports == null) {
                  return ''
                }

                return (
                  <div>
                    {course.cas_supports.map((i) => (
                      <div key={i.id}>- {i.name}</div>
                    ))}
                  </div>
                )
              }
            },
            {
              title: 'Software',
              render: (_, course) => {
                if (course.software == null) {
                  return ''
                }

                return <div>{course.software.name}</div>
              }
            },
            {
              title: 'Case-uploading',
              render: (_, course) => {
                if (course.case_uploaders == null) {
                  return ''
                }

                return (
                  <div>
                    {course.case_uploaders.map((i) => (
                      <div key={i.id}>- {i.name}</div>
                    ))}
                  </div>
                )
              }
            },
            {
              title: 'Start date',
              render: (course) => <LocalDateTime date={course.start_at} timezone={course.timezone} />,
              sorter: (a, b) => compareDate(a.start_at, b.start_at)
            },
            {
              title: 'End date',
              render: (course) => <LocalDateTime date={course.end_at} timezone={course.timezone} />,
              sorter: (a, b) => compareDate(a.end_at, b.end_at)
            },
            {
              title: '# of cases',
              render: (course) => {
                return course?.uploaded_cases.length
              }
            },
            {
              title: '',
              render: (_, course) => {
                return (
                  <Space size="middle">
                    <Link to={`${course.id}`}>
                      <Button type="primary" icon={<EyeOutlined />} />
                    </Link>
                  </Space>
                )
              }
            }
          ]}
        />
      </Card>
    </>
  )
}

export default CourseTable
